import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconWallet } from '@tabler/icons-react';
import { fetchReembolsosOverview, updateExpenseStatus } from '../services/reembolsosService';
import { statusKeyFromAction } from '../utils/reembolsosStatus';
import { getReembolsoUiPerms } from '../../../utils/userFinanceiroAccess';
import GestaoReembolsosOverview from './GestaoReembolsosOverview';
import GestaoReembolsosDetail from './GestaoReembolsosDetail';
import GestaoReembolsosSkeleton from './GestaoReembolsosSkeleton';
import './GestaoReembolsosPage.css';

const ACTION_PROGRESS_LABEL = {
  approve: 'Aprovando',
  pay: 'Confirmando pagamento',
  reject_definitive: 'Negando',
  reject_review: 'Rejeitando',
};

/**
 * Gestão de reembolsos — dados MS SQL (LCMY_GO_EXPENSES).
 * Overview: coordenador → área → técnicos.
 * Detail: lançamentos do técnico.
 */
const GestaoReembolsosPage = ({ currentUser = null }) => {
  const reembolsoPerms = useMemo(() => getReembolsoUiPerms(currentUser), [currentUser]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [view, setView] = useState('overview');
  const [selectedTech, setSelectedTech] = useState(null);
  /** Patches locais de status (otimista + pós-ação até refresh) */
  const [expensePatches, setExpensePatches] = useState({});
  const [flashIds, setFlashIds] = useState(() => new Set());
  /** @type {null | { ids: string[], label: string, count: number }} */
  const [actionProgress, setActionProgress] = useState(null);

  const load = useCallback(async ({ soft = false, quiet = false } = {}) => {
    if (soft) {
      if (!quiet) setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const res = await fetchReembolsosOverview('all');
      setData(res);
      setExpensePatches({});
    } catch (err) {
      setError(err?.message || 'Erro ao carregar reembolsos');
      if (!soft) setData(null);
    } finally {
      if (soft && !quiet) setRefreshing(false);
      if (!soft) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const expensesMerged = useMemo(() => {
    const list = data?.expenses || [];
    if (!Object.keys(expensePatches).length) return list;
    return list.map((e, idx) => {
      const id =
        e.id != null && String(e.id) !== ''
          ? String(e.id)
          : `${e.code || 'x'}-${e.expenseDate || ''}-${idx}`;
      const p = expensePatches[id] || expensePatches[String(e.id)];
      if (!p) return e;
      return {
        ...e,
        statusKey: p.statusKey,
        rejectReason: p.reviewNote || e.rejectReason,
        reviewNote: p.reviewNote || e.reviewNote,
        hasTimeline: p.hasTimeline != null ? p.hasTimeline : e.hasTimeline,
      };
    });
  }, [data, expensePatches]);

  const techContext = useMemo(() => {
    if (!selectedTech || !data) return selectedTech;
    const pad = (s) => {
      const t = String(s || '').trim();
      return /^\d+$/.test(t) ? t.padStart(4, '0') : t;
    };
    const selCod = pad(selectedTech.codtech);
    for (const c of data.coordinators || []) {
      for (const a of c.areas || []) {
        for (const t of a.technicians || []) {
          if (
            (selCod && pad(t.codtech) === selCod) ||
            String(t.codtech) === String(selectedTech.codtech)
          ) {
            return {
              ...t,
              coordinatorName: c.name,
              areaName: a.name,
            };
          }
        }
      }
    }
    return selectedTech;
  }, [selectedTech, data]);

  const techExpenses = useMemo(() => {
    if (!techContext) return [];
    const code = String(techContext.codtech || '').trim();
    const name = String(techContext.name || '').trim();
    const pad = (s) => (/^\d+$/.test(s) ? s.padStart(4, '0') : s);
    return expensesMerged.filter((e) => {
      const expCod = String(e.codtech || '').trim();
      if (code && expCod && (code === expCod || pad(code) === pad(expCod))) return true;
      if (!code && name && String(e.techName || '').trim() === name) return true;
      return false;
    });
  }, [techContext, expensesMerged]);

  const openTech = (tech) => {
    let coordinatorName = '';
    let areaName = '';
    const pad = (s) => {
      const t = String(s || '').trim();
      return /^\d+$/.test(t) ? t.padStart(4, '0') : t;
    };
    const techCod = pad(tech.codtech);
    for (const c of data?.coordinators || []) {
      for (const a of c.areas || []) {
        if (
          (a.technicians || []).some((t) => {
            if (techCod && pad(t.codtech) === techCod) return true;
            return String(t.codtech) === String(tech.codtech);
          })
        ) {
          coordinatorName = c.name;
          areaName = a.name;
          break;
        }
      }
    }
    setSelectedTech({ ...tech, coordinatorName, areaName });
    setView('detail');
  };

  const softRefresh = useCallback(() => load({ soft: true }), [load]);

  const handleStatusChange = useCallback(
    async (ids, { action, reason } = {}) => {
      const idList = (ids || []).map(String).filter(Boolean);
      if (!idList.length || !action) return { ok: false };
      const label = ACTION_PROGRESS_LABEL[action] || 'Processando';
      setActionProgress({ ids: idList, label, count: idList.length });
      setActionError(null);
      try {
        const dataRes = await updateExpenseStatus({
          ids: idList.map((id) => Number(id)),
          action,
          reason,
        });
        const nextKey = statusKeyFromAction(action);
        setExpensePatches((prev) => {
          const next = { ...prev };
          for (const raw of idList) {
            next[String(raw)] = {
              statusKey: nextKey || prev[String(raw)]?.statusKey,
              reviewNote: reason || prev[String(raw)]?.reviewNote,
              hasTimeline: true,
            };
          }
          return next;
        });
        setFlashIds(new Set(idList));
        window.setTimeout(() => setFlashIds(new Set()), 1400);
        // refresh sem ofuscamento — o feedback da ação já cobre a espera
        await load({ soft: true, quiet: true });
        return { ok: true, data: dataRes };
      } catch (err) {
        setActionError(err?.message || 'Falha ao atualizar status');
        return { ok: false, message: err?.message };
      } finally {
        setActionProgress(null);
      }
    },
    [load]
  );

  const actionBusy = Boolean(actionProgress);
  const processingIds = useMemo(
    () => new Set(actionProgress?.ids || []),
    [actionProgress]
  );

  return (
    <div className="gr-module">
      <header className="gr-header">
        <nav className="gr-breadcrumb" aria-label="Localização em financeiro">
          <span className="gr-breadcrumb-avatar" aria-hidden="true">F</span>
          <span className="gr-breadcrumb-segment gr-breadcrumb-segment--parent">Financeiro</span>
          <span className="gr-breadcrumb-sep" aria-hidden="true">/</span>
          <span className="gr-breadcrumb-current" aria-current="page">
            <IconWallet size={13} stroke={1.75} aria-hidden className="gr-breadcrumb-current-icon" />
            <span className="gr-breadcrumb-current-text">Gestão de reembolsos</span>
          </span>
        </nav>
      </header>

      <div className={`gestao-reembolsos-root${data ? ' has-filters' : ''}`}>
        {loading && !data ? <GestaoReembolsosSkeleton variant="overview" withToolbar /> : null}
        {error && !data ? (
          <div className="gestao-reembolsos-load-error" role="alert">{error}</div>
        ) : null}
        {actionError ? (
          <div className="gr-action-toast" role="alert">
            <span>{actionError}</span>
            <button type="button" className="gr-action-toast-close" onClick={() => setActionError(null)}>
              ✕
            </button>
          </div>
        ) : null}
        {data ? (
          <div
            className={`gr-live-host${refreshing ? ' is-skeleton' : ''}${actionBusy ? ' is-action-busy' : ''}`}
            data-gr-view={view}
          >
            {view === 'overview' ? (
              <GestaoReembolsosOverview
                coordinators={data.coordinators || []}
                expenses={expensesMerged}
                selectedCodtech={null}
                onOpenTech={openTech}
                onRefresh={softRefresh}
                refreshing={refreshing}
                canCreateExpense={reembolsoPerms.canCreateExpense}
              />
            ) : (
              <GestaoReembolsosDetail
                tech={techContext}
                expenses={techExpenses}
                refreshing={refreshing}
                onRefresh={softRefresh}
                onStatusChange={handleStatusChange}
                flashIds={flashIds}
                actionBusy={actionBusy}
                processingIds={processingIds}
                canApproveReject={reembolsoPerms.canApproveReject}
                canPay={reembolsoPerms.canPay}
                onBack={() => {
                  setSelectedTech(null);
                  setView('overview');
                }}
              />
            )}
            {refreshing ? <GestaoReembolsosSkeleton variant={view} overlay /> : null}
            {loading && !refreshing ? <div className="gr-live-loading-bar" aria-hidden /> : null}
            {actionBusy ? (
              <div className="gr-action-progress" role="status" aria-live="polite">
                <div className="gr-action-progress-track" aria-hidden>
                  <div className="gr-action-progress-indeterminate" />
                </div>
                <div className="gr-action-progress-pill">
                  <span className="gr-action-progress-spinner" aria-hidden />
                  <span className="gr-action-progress-text">
                    {actionProgress.label}
                    {actionProgress.count > 1
                      ? ` ${actionProgress.count} lançamentos`
                      : ' lançamento'}
                    …
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GestaoReembolsosPage;
